
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface MortgageData {
  loanAmount: number;
  interestRate: number;
  loanTerm: number;
  downPayment: number;
  propertyTax: number;
  homeInsurance: number;
  pmi: number;
  propertyLocation: "spain" | "other";
  propertyCondition: "new" | "used";
}

interface MonthlyPayment {
  principalAndInterest: number;
  propertyTax: number;
  homeInsurance: number;
  pmi: number;
  total: number;
}

interface TaxesAndExpenses {
  notary: number;
  registry: number;
  management: number;
  taxes: number;
  appraisal: number;
  total: number;
}

interface AmortizationEntry {
  month: number;
  payment: number;
  principal: number;
  interest: number;
  balance: number;
  totalInterest: number;
}

declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => void;
  }
}

export const generateMortgagePDF = (
  data: MortgageData,
  monthlyPayment: MonthlyPayment,
  amortizationSchedule: AmortizationEntry[],
  taxesAndExpenses: TaxesAndExpenses | null,
  t: (key: string) => string,
  currency: string
) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
  
  // Currency formatting
  const formatCurrency = (amount: number) => {
    const formattedAmount = amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    
    if (currency === "USD") {
      return `$${formattedAmount}`;
    } else if (currency === "GBP") {
      return `£${formattedAmount}`;
    } else if (currency === "CHF") {
      return `CHF ${formattedAmount}`;
    } else {
      // EUR - symbol goes to the right
      return `${formattedAmount} €`;
    }
  };

  // Header
  doc.setFontSize(20);
  doc.setTextColor(234, 88, 12); // #EA580C
  doc.text(t('tools.mortgage.title'), pageWidth / 2, 20, { align: 'center' });
  
  // Date
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`${t('common.generatedOn')}: ${new Date().toLocaleDateString()}`, pageWidth / 2, 30, { align: 'center' });

  // Loan Details
  doc.setFontSize(14);
  doc.setTextColor(0);
  doc.text(t('tools.mortgage.loanDetails'), 20, 50);
  
  const loanDetails = [
    [t('tools.mortgage.loanAmount'), formatCurrency(data.loanAmount)],
    [t('tools.mortgage.downPayment'), formatCurrency(data.downPayment)],
    [t('tools.mortgage.principalAmount'), formatCurrency(data.loanAmount - data.downPayment)],
    [t('tools.mortgage.interestRate'), `${data.interestRate}%`],
    [t('tools.mortgage.loanTerm'), `${data.loanTerm} ${t('tools.mortgage.years')}`],
    [t('tools.mortgage.propertyTax'), formatCurrency(data.propertyTax)],
    [t('tools.mortgage.homeInsurance'), formatCurrency(data.homeInsurance)],
    [t('tools.mortgage.pmi'), formatCurrency(data.pmi)]
  ];

  autoTable(doc, {
    startY: 60,
    head: [[t('tools.mortgage.detail'), t('common.amount')]],
    body: loanDetails,
    theme: 'grid',
    headStyles: { fillColor: [234, 88, 12] },
    margin: { left: 20, right: 20 }
  });

  // Monthly Payment Breakdown
  const finalY1 = (doc as any).lastAutoTable.finalY + 20;
  doc.setFontSize(14);
  doc.text(t('tools.mortgage.monthlyPaymentBreakdown'), 20, finalY1);
  
  const paymentBreakdown = [
    [t('tools.mortgage.principalAndInterest'), formatCurrency(monthlyPayment.principalAndInterest)],
    [t('tools.mortgage.propertyTax'), formatCurrency(monthlyPayment.propertyTax)],
    [t('tools.mortgage.homeInsurance'), formatCurrency(monthlyPayment.homeInsurance)],
    [t('tools.mortgage.pmi'), formatCurrency(monthlyPayment.pmi)],
    [t('tools.mortgage.totalMonthlyPayment'), formatCurrency(monthlyPayment.total)]
  ];

  autoTable(doc, {
    startY: finalY1 + 10,
    head: [[t('tools.mortgage.component'), t('common.amount')]],
    body: paymentBreakdown,
    theme: 'grid',
    headStyles: { fillColor: [234, 88, 12] },
    margin: { left: 20, right: 20 }
  });

  // Loan Summary
  const finalY2 = (doc as any).lastAutoTable.finalY + 20;
  doc.setFontSize(14);
  doc.text(t('tools.mortgage.loanSummary'), 20, finalY2);
  
  const totalInterest = amortizationSchedule[amortizationSchedule.length - 1]?.totalInterest || 0;
  const totalPayments = monthlyPayment.principalAndInterest * data.loanTerm * 12;
  
  const loanSummary = [
    [t('tools.mortgage.totalLoanAmount'), formatCurrency(data.loanAmount - data.downPayment)],
    [t('tools.mortgage.totalInterest'), formatCurrency(totalInterest)],
    [t('tools.mortgage.totalPayments'), formatCurrency(totalPayments)],
    [t('tools.mortgage.downPaymentPercent'), `${((data.downPayment / data.loanAmount) * 100).toFixed(1)}%`]
  ];

  autoTable(doc, {
    startY: finalY2 + 10,
    head: [[t('tools.mortgage.detail'), t('common.amount')]],
    body: loanSummary,
    theme: 'grid',
    headStyles: { fillColor: [234, 88, 12] },
    margin: { left: 20, right: 20 }
  });

  // Taxes and Expenses
  if (taxesAndExpenses) {
    const finalY3 = (doc as any).lastAutoTable.finalY + 20;
    doc.setFontSize(14);
    doc.text(t('tools.mortgage.taxesAndExpenses'), 20, finalY3);
    
    const taxesExpensesData = [
      [t('tools.mortgage.notary'), formatCurrency(taxesAndExpenses.notary)],
      [t('tools.mortgage.registry'), formatCurrency(taxesAndExpenses.registry)],
      [t('tools.mortgage.management'), formatCurrency(taxesAndExpenses.management)],
      [t('tools.mortgage.taxes'), formatCurrency(taxesAndExpenses.taxes)],
      [t('tools.mortgage.appraisal'), formatCurrency(taxesAndExpenses.appraisal)],
      [t('tools.mortgage.totalExpenses'), formatCurrency(taxesAndExpenses.total)]
    ];

    autoTable(doc, {
      startY: finalY3 + 10,
      head: [[t('tools.mortgage.component'), t('common.amount')]],
      body: taxesExpensesData,
      theme: 'grid',
      headStyles: { fillColor: [234, 88, 12] },
      margin: { left: 20, right: 20 }
    });

    // Warning note
    const finalY4 = (doc as any).lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.setTextColor(180, 87, 12);
    const warningText = t('tools.mortgage.orientativeCalculationWarning');
    const splitWarning = doc.splitTextToSize(warningText, pageWidth - 40);
    doc.text(splitWarning, 20, finalY4);
  }

  // New page for amortization schedule
  doc.addPage();
  
  doc.setFontSize(16);
  doc.setTextColor(234, 88, 12);
  doc.text(t('tools.mortgage.amortizationSchedule'), pageWidth / 2, 20, { align: 'center' });

  // Amortization table (first 12 months on first page)
  const amortizationData = amortizationSchedule.slice(0, 12).map(entry => [
    entry.month.toString(),
    formatCurrency(entry.payment),
    formatCurrency(entry.principal),
    formatCurrency(entry.interest),
    formatCurrency(entry.balance)
  ]);

  autoTable(doc, {
    startY: 40,
    head: [[
      t('tools.mortgage.month'),
      t('tools.mortgage.payment'),
      t('tools.mortgage.principal'),
      t('tools.mortgage.interest'),
      t('tools.mortgage.balance')
    ]],
    body: amortizationData,
    theme: 'grid',
    headStyles: { fillColor: [234, 88, 12] },
    margin: { left: 10, right: 10 },
    styles: { fontSize: 8 }
  });

  // Add note about full schedule
  if (amortizationSchedule.length > 12) {
    const finalY3 = (doc as any).lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(t('tools.mortgage.scheduleNote'), 20, finalY3);
  }

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text('Generated by Finanzly - Your Financial Planning Partner', pageWidth / 2, doc.internal.pageSize.height - 10, { align: 'center' });

  // Save the PDF
  doc.save(`mortgage-calculation-${new Date().toISOString().split('T')[0]}.pdf`);
  
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('Error al generar el PDF. Por favor, inténtalo de nuevo.');
  }
};
